/**
 * Choose an exercise by typing, for the Progress screen.
 *
 * Not ExercisePicker, which is built for adding to a workout: it offers to create
 * a custom exercise (nothing to create here — you are looking something up) and
 * hides archived exercises (which Progress must keep reachable, or removing a
 * mis-entered exercise makes its records impossible to look at).
 *
 * With the box empty, what you have actually trained comes first, most recent
 * at the top — the exercise you want to look at is nearly always one you did
 * this week, not Russian Twist. Everything else follows alphabetically.
 */

import { useEffect, useMemo, useRef, useState } from 'react'

import type { Exercise } from '../db'

const MAX_ROWS = 40

export function ExerciseSearch({
  exercises,
  lastTrained,
  value,
  onChange,
}: {
  exercises: Exercise[]
  /** exercise id → ISO time of its most recent set. */
  lastTrained: Map<number, string>
  value: number | null
  onChange: (id: number) => void
}) {
  const chosen = exercises.find((e) => e.id === value)
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)
  const input = useRef<HTMLInputElement>(null)

  // Close on a tap anywhere else. Not onBlur: on a phone the input blurs on the
  // touch that lands on a result, before that result's click fires, so the list
  // would vanish out from under the finger.
  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', onDown)
    return () => document.removeEventListener('pointerdown', onDown)
  }, [open])

  const q = query.trim().toLowerCase()

  const matches = useMemo(() => {
    // A removed exercise stays findable only if there is something to look at.
    const candidates = exercises.filter((e) => !e.archived_at || lastTrained.has(e.id))
    const filtered = q
      ? candidates.filter((e) => e.name.toLowerCase().includes(q) || e.slug.includes(q))
      : candidates
    return [...filtered]
      .sort((a, b) => {
        const la = lastTrained.get(a.id)
        const lb = lastTrained.get(b.id)
        if (la && lb) return la < lb ? 1 : la > lb ? -1 : 0
        if (la) return -1
        if (lb) return 1
        return a.name.localeCompare(b.name)
      })
      .slice(0, MAX_ROWS)
  }, [exercises, lastTrained, q])

  const pick = (id: number) => {
    onChange(id)
    setQuery('')
    setOpen(false)
    input.current?.blur()
  }

  return (
    <div ref={root}>
      <label htmlFor="progress-exercise-search">Exercise</label>
      <input
        id="progress-exercise-search"
        ref={input}
        type="search"
        // Showing the choice in the box itself, until you start typing over it.
        value={open ? query : chosen?.name ?? ''}
        placeholder={chosen ? chosen.name : 'Search exercises…'}
        onFocus={() => { setQuery(''); setOpen(true) }}
        onChange={(e) => { setQuery(e.target.value); setOpen(true) }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { setOpen(false); input.current?.blur() }
          if (e.key === 'Enter' && matches[0]) pick(matches[0].id)
        }}
        autoComplete="off"
        autoCapitalize="none"
      />

      {open && (
        <ul className="plain" style={{ maxHeight: '20rem', overflowY: 'auto', marginBottom: 0 }}>
          {matches.map((e) => (
            <li
              key={e.id}
              className="tappable"
              onClick={() => pick(e.id)}
              style={{ padding: '.7rem .5rem' }}
            >
              <span>
                {e.name}
                {e.archived_at && <span className="muted"> (removed)</span>}
              </span>
              <span className="muted" style={{ fontSize: '.85rem' }}>
                {lastTrained.has(e.id) ? shortDate(lastTrained.get(e.id)!) : e.equipment}
              </span>
            </li>
          ))}
          {matches.length === 0 && <li className="muted">No exercise matches “{query}”.</li>}
        </ul>
      )}
    </div>
  )
}

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' })
}
